import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { MeasurementDatesService } from 'api';
import {
  MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_DEFAULT_ERROR_MESSAGE,
  MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import {
  GetMeasurementDateRowsByFirmDateParams,
  GetMeasurementDateRowsByFirmDateResponse,
  GetMeasurementDateRowsByFirmDateReturn,
  UseGetMeasurementDateRowsByFirmDateParams,
} from './useGetMeasurementDateRowsByFirmDate.types';

export const getMeasurementDateRowsByFirmDate = async (
  params: GetMeasurementDateRowsByFirmDateParams
): Promise<GetMeasurementDateRowsByFirmDateResponse | null> => {
  const { firmId, measurementDate } = params;

  if (isNil(firmId) || isNil(measurementDate)) {
    return throwError(MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const measurementDateRows = await MeasurementDatesService.apiMeasurementDatesFirmDateRead(
      toString(firmId),
      measurementDate
    );

    if (!measurementDateRows) {
      return throwError(MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_DEFAULT_ERROR_MESSAGE);
    }

    return measurementDateRows;
  } catch (error) {
    return throwError(MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetMeasurementDateRowsByFirmDate = (
  params: UseGetMeasurementDateRowsByFirmDateParams
): GetMeasurementDateRowsByFirmDateReturn => {
  const { firmId, measurementDate, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetMeasurementDateRowsByFirmDateReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && !isNil(measurementDate) && shouldQueryAutomatically,
    queryFn: () => getMeasurementDateRowsByFirmDate({ firmId, measurementDate }),
    queryKey: [MEASUREMENT_DATE_ROWS_BY_FIRM_DATE_QUERY_KEY, firmId, measurementDate],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetMeasurementDateRowsByFirmDate;
